import { useEffect, useState } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Spinner,
  Text,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  Mail24Regular,
  Megaphone24Regular,
  Person24Regular,
  BuildingShop24Regular,
  Save24Regular,
  SignOut24Regular,
} from "@fluentui/react-icons";
import { getConfig, saveConfig } from "./api";
import { getAccountName, logout } from "./auth";
import RulesList from "./views/RulesList";
import RuleWizard from "./views/RuleWizard";
import CampaignsTab from "./tabs/CampaignsTab";
import OverridesTab from "./tabs/OverridesTab";
import CompanyTab from "./tabs/CompanyTab";

const NAV = [
  { key: "signatures", label: "Signatures", icon: <Mail24Regular /> },
  { key: "campaigns", label: "Campaigns", icon: <Megaphone24Regular /> },
  { key: "overrides", label: "User overrides", icon: <Person24Regular /> },
  { key: "company", label: "Company", icon: <BuildingShop24Regular /> },
];

const useStyles = makeStyles({
  root: { display: "flex", height: "100vh", fontFamily: tokens.fontFamilyBase },
  sidebar: {
    width: "220px",
    flexShrink: 0,
    backgroundColor: "#0f2e1d",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    paddingTop: "8px",
  },
  brand: { padding: "16px 20px 20px", fontSize: "18px", fontWeight: tokens.fontWeightSemibold, color: "#fff" },
  brandAccent: { color: "#5fc88f" },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 20px",
    cursor: "pointer",
    color: "#cfe3d6",
    ...shorthands.borderLeft("3px", "solid", "transparent"),
  },
  navItemActive: {
    backgroundColor: "#163e28",
    color: "#fff",
    ...shorthands.borderLeft("3px", "solid", "#5fc88f"),
  },
  main: { flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#faf9f8" },
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    backgroundColor: "#fff",
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
  },
  content: { flexGrow: 1, overflowY: "auto", padding: "24px" },
  right: { display: "flex", alignItems: "center", gap: "12px" },
  centered: { display: "flex", alignItems: "center", justifyContent: "center", height: "100%" },
});

export default function App() {
  const styles = useStyles();
  const [config, setConfig] = useState(null);
  const [nav, setNav] = useState("signatures");
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(null); // rule index being edited, -1 = new, null = none

  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch((e) => setStatus({ type: "error", text: `Couldn't load config: ${e.message}` }));
  }, []);

  const update = (section) => (value) => {
    setConfig((c) => ({ ...c, [section]: value }));
    setDirty(true);
  };

  const persist = async (next) => {
    const payload = next || config;
    setSaving(true);
    setStatus(null);
    try {
      await saveConfig(payload);
      setDirty(false);
      setStatus({ type: "success", text: "Published. Changes are live." });
    } catch (e) {
      setStatus({ type: "error", text: `Publish failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <div className={styles.centered}>
        {status ? (
          <MessageBar intent="error"><MessageBarBody>{status.text}</MessageBarBody></MessageBar>
        ) : (
          <Spinner label="Loading…" />
        )}
      </div>
    );
  }

  const navMeta = NAV.find((n) => n.key === nav);
  const account = getAccountName();

  return (
    <div className={styles.root}>
      <nav className={styles.sidebar}>
        <div className={styles.brand}>
          Davey <span className={styles.brandAccent}>Signatures</span>
        </div>
        {NAV.map((n) => (
          <div
            key={n.key}
            className={`${styles.navItem} ${nav === n.key ? styles.navItemActive : ""}`}
            onClick={() => setNav(n.key)}
          >
            {n.icon}
            <span>{n.label}</span>
          </div>
        ))}
      </nav>

      <div className={styles.main}>
        <div className={styles.topbar}>
          <Text size={500} weight="semibold">{navMeta?.label}</Text>
          <div className={styles.right}>
            {dirty && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>Unpublished changes</Text>}
            <Button appearance="primary" icon={<Save24Regular />} onClick={() => persist()} disabled={saving || !dirty}>
              {saving ? "Publishing…" : "Publish all"}
            </Button>
            {account && (
              <>
                <Text size={200} title={account}>{account}</Text>
                <Button appearance="subtle" icon={<SignOut24Regular />} title="Sign out" onClick={() => logout()}>
                  Sign out
                </Button>
              </>
            )}
          </div>
        </div>

        {status && (
          <MessageBar intent={status.type === "error" ? "error" : "success"} style={{ margin: "8px 24px 0" }}>
            <MessageBarBody>{status.text}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.content}>
          {nav === "signatures" && (
            <RulesList
              assignments={config.assignments}
              templates={config.templates}
              onChange={update("assignments")}
              onEdit={(i) => setEditing(i)}
            />
          )}
          {nav === "campaigns" && <CampaignsTab value={config.campaigns} onChange={update("campaigns")} />}
          {nav === "overrides" && (
            <OverridesTab value={config.overrides} templates={config.templates.templates} onChange={update("overrides")} />
          )}
          {nav === "company" && <CompanyTab value={config.company} onChange={update("company")} />}
        </div>
      </div>

      {editing !== null && (
        <RuleWizard
          index={editing}
          assignments={config.assignments}
          templates={config.templates}
          onApply={(nextAssignments, nextTemplates) => {
            setConfig((c) => ({ ...c, assignments: nextAssignments, templates: nextTemplates }));
            setDirty(true);
          }}
          onPublish={(nextAssignments, nextTemplates) => {
            const next = { ...config, assignments: nextAssignments, templates: nextTemplates };
            setConfig(next);
            persist(next);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
